import React, {useCallback, useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Button, Checkbox,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControlLabel, FormGroup,
    Grid, RadioGroup, TextField
} from "@mui/material";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";

import jpActions from "../../../../actions/jpActions";
import { getJobPlanActivityTypes, getJobPlanActivityCategories, getJobPlanActivityLocations, getJobPlanActivityObjectives } from "../../../../reducers";

import ActivityTimetabled from "./ActivityTimetabled";
import ActivityFlexible from "./ActivityFlexible";

import { convertHoursMinsToDec, convertDecToHoursMins } from "../functions";


const appCode = 'P-JP';

function ActivityFormDialog({jpId, open, onClose, data = {}}) {
    const isNew = !data?.id;
    const dispatch = useDispatch();
    const types = useSelector(getJobPlanActivityTypes);
    const categories = useSelector(getJobPlanActivityCategories);
    const locations = useSelector(getJobPlanActivityLocations);
    const objectives = useSelector(getJobPlanActivityObjectives) || [];

    useEffect(() => {
        dispatch(jpActions.getJobPlanActivityObjectives(appCode, jpId));
    }, [dispatch, jpId]);

    const [type, setType] = useState(data?.type?.code || '');
    const [category, setCategory] = useState(data?.category?.code || '');
    const [subcategory, setSubcategory] = useState(data?.subcategory?.id || '');
    const [description, setDescription] = useState(data?.description || '');
    const [location, setLocation] = useState(data?.location?.id || '');
    const [onCall, setOnCall] = useState(data?.onCall || false);
    const [scheduleType, setScheduleType] = useState(!data?.flexible ? 'scheduled' : 'flexible');
    const [schedule, setSchedule] = useState(data?.scheduled || {});
    const [flexibleHours, setFlexibleHours] = useState(!data.flexible ? {} : {
        frequency: data?.annualOccurrences || null,
        ...convertDecToHoursMins(data?.duration)
    });
    const [objective, setObjective] = useState(data?.objective?.id || '');

    const handleTimetabledChange = useCallback(({target}) => setScheduleType(target.value), []);
    const handleSetSchedule = useCallback((newSchedule) => setSchedule(newSchedule), []);
    const handleSetFlexibleHours = useCallback((newFlexibleHours) => setFlexibleHours(newFlexibleHours), []);

    const onSubmit = () => {
        const selectedCategory = categories.find(({code}) => code === category);
        const flexible = scheduleType === 'flexible';
        const params = {
            description,
            type: types.find(({ code }) => code === type),
            category: {
                id: selectedCategory.id,
                code: selectedCategory.code,
                name: selectedCategory.name,
            },
            subcategory: !subcategory ? null : selectedCategory.subcategories.find(({id}) => Number(id) === Number(subcategory)),
            location: locations.find(({id}) => Number(id) === Number(location)),
            onCall,
            flexible,
            scheduled: !flexible ? schedule : {},
            duration: flexible ? convertHoursMinsToDec(flexibleHours.hours, flexibleHours.minutes) : null,
            annualOccurrences: flexible ? Number(flexibleHours?.frequency) : null,
            objective: objectives.find(({id}) => Number(id) === Number(objective))
        };
        if (isNew) {
            dispatch(jpActions.addJobPlanActivity(appCode, jpId, params));
        } else {
            dispatch(jpActions.updateJobPlanActivity(appCode, data.id, jpId, params));
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="md">
            <DialogTitle>{isNew ? "New activity" : "Edit activity"}</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                >
                    <DialogContentText
                        id="scroll-dialog-description"
                        tabIndex={-1}
                        component="div"
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    variant="outlined"
                                    id="select-type"
                                    select
                                    label="Type"
                                    aria-label="Type"
                                    helperText="select the type of activity"
                                    onChange={({target}) => setType(target.value)}
                                    autoFocus
                                    fullWidth
                                    required
                                    defaultValue="PA"
                                >
                                    {types.map((type, index) => (
                                        <MenuItem key={`o_t_${index}`} value={type.code} >
                                            {`${type.name} (${type.code})`}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    variant="outlined"
                                    id="select-category"
                                    select
                                    label="Category"
                                    helperText="select a category"
                                    onChange={({target}) => {
                                        setCategory(target.value);
                                        setSubcategory("");
                                    }}
                                    fullWidth
                                    required
                                    value={category}
                                >
                                    {categories.map((option, index) => (
                                        <MenuItem key={`o_c_${index}`} value={option.code} >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            {!!category && (
                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        variant="outlined"
                                        id="select-subcategory"
                                        select
                                        label="Subcategory"
                                        helperText="select a subcategory"
                                        onChange={({target}) => setSubcategory(target.value)}
                                        fullWidth
                                        value={subcategory}
                                    >
                                        {(categories.find(({code}) => code === category)?.subcategories || [])
                                            .map((option, index) => (
                                                <MenuItem key={`o_sc_${index}`} value={option.id} >
                                                    {option.name}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </Grid>
                            )}

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    variant="outlined"
                                    id="text-description"
                                    value={description}
                                    onChange={({target}) => setDescription(target?.value)}
                                    label="Description"
                                    placeholder="Description"
                                    helperText="describe this activity"
                                    required
                                    multiline
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    variant="outlined"
                                    id="select-location"
                                    select
                                    label="Location"
                                    helperText="select a location"
                                    onChange={({target}) => setLocation(target.value)}
                                    fullWidth
                                    required
                                    value={location}
                                >
                                    {locations.map((option, index) => (
                                        <MenuItem key={`o_l_${index}`} value={option.id} >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={6} sm={6} md={6}>
                                <FormGroup>
                                    <FormControlLabel
                                        label="On Call"
                                        control={
                                            <Checkbox
                                                defaultChecked={onCall}
                                                onChange={({target}) => setOnCall(target.checked)}
                                            />
                                        }
                                    />
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="radio-schedule"
                                        aria-label="schedule"
                                        value={scheduleType}
                                        onChange={handleTimetabledChange}
                                    >
                                        <FormControlLabel
                                            value="scheduled"
                                            control={<Radio />}
                                            label="Scheduled"
                                        />
                                        <FormControlLabel
                                            value="annualised"
                                            control={<Radio />}
                                            label="Annualised"
                                        />
                                        <FormControlLabel
                                            value="flexible"
                                            control={<Radio />}
                                            label="Flexible"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {scheduleType === 'flexible'
                            ? <ActivityFlexible flexibleHours={flexibleHours} setFlexibleHours={handleSetFlexibleHours} />
                            : <ActivityTimetabled schedule={schedule} setSchedule={handleSetSchedule} />
                        }

                        <Grid container spacing={3}>
                            <Grid item xs={6} sm={6} md={6}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Checkbox />}
                                        label="Prospective"
                                    />
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    id="select-objectives"
                                    select
                                    label="Objectives"
                                    helperText="link to an objective"
                                    onChange={({target}) => setObjective(target.value)}
                                    fullWidth
                                    value={objective}
                                >
                                    {objectives.map((option, index) => (
                                        <MenuItem key={`o_o_${index}`} value={option.id} >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    key="save"
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    disabled={
                        !description
                        || !category
                        || !location
                        || (scheduleType === 'flexible' && (!flexibleHours.hours || !flexibleHours.frequency))
                        || (scheduleType === 'scheduled' && (!schedule.start || !schedule.end || !schedule.dayOfWeek?.length))
                    }
                >
                    {!data?.id ? "Add" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ActivityFormDialog;
